// Virtual sub-accounts layered over the single real Kalshi account; tree derived from space-split display names.

import { allFills, getMeta, setMeta, type FillRow } from './db';
import type { Cash, MC, MQ } from './money';
import type { AccountRow, PnlTreeNode } from './types';

export const MOBILE_ID = 'J-00000'; // reserved residual (external/phone/unattributed)
export const MOBILE_NAME = 'mobile';

export function fmtId(n: number): string {
  return `J-${String(n).padStart(5, '0')}`;
}

export class AccountError extends Error {}

/** Per-(account, ticker) average-cost state; `posMq > 0` is long YES, `< 0` is long NO, `costCash` same sign. */
export interface PosState {
  posMq: MQ;
  costCash: Cash;
  realizedCash: Cash;
  feesCash: Cash;
}

export function blankPos(): PosState {
  return { posMq: 0, costCash: 0, realizedCash: 0, feesCash: 0 };
}

/** YES-equivalent average entry price in MC, or 0 when flat. */
export function avgPxMc(ps: PosState): number {
  if (ps.posMq === 0) return 0;
  return ps.costCash / ps.posMq;
}

/** Cash cost basis of the open position, regardless of side; always >= 0. */
export function costOf(ps: PosState): Cash {
  if (ps.posMq === 0) return 0;
  if (ps.posMq > 0) return ps.costCash;
  return Math.abs(ps.posMq) * 1000 - Math.abs(ps.costCash);
}

/** Signed average-cost update, in place; a NO buy is PnL-equivalent to selling YES at the same yes-price. */
export function applyFill(ps: PosState, side: 'yes' | 'no', mq: MQ, yesPriceMc: MC, feeCash: Cash): void {
  const dq = side === 'yes' ? mq : -mq;
  ps.feesCash += feeCash;
  const pos = ps.posMq;

  if (pos === 0 || (pos > 0) === (dq > 0)) {
    ps.posMq = pos + dq;
    ps.costCash += dq * yesPriceMc; // signed; cost/pos stays the YES-equiv average
    if (ps.posMq === 0) ps.costCash = 0;
    return;
  }

  // Reducing / closing / flipping.
  const closing = Math.min(Math.abs(dq), Math.abs(pos));
  const dir = pos > 0 ? 1 : -1;
  // realized = dir * closing * (px - avg); costPart is that exact slice of costCash removed.
  const costPart = Math.round((ps.costCash * closing) / Math.abs(pos));
  ps.realizedCash += dir * closing * yesPriceMc - costPart;

  const newPos = pos + dq;
  if (newPos === 0) ps.costCash = 0;
  else if ((newPos > 0) !== (pos > 0)) ps.costCash = newPos * yesPriceMc; // flipped -> new basis
  else ps.costCash -= costPart;
  ps.posMq = newPos;
}

export interface Account {
  id: string;             // J-NNNNN
  name: string;           // display name, e.g. "KXWCGAME Manual1"
  parts: string[];        // ("KXWCGAME", "Manual1") — the leaf path
  positions: Map<string, PosState>; // ticker -> state, derived from the ledger
}

export function isMobile(a: Account): boolean {
  return a.id === MOBILE_ID;
}

interface Registry {
  next_id: number;
  accounts: Record<string, { name: string; parts: string[] }>;
}

function splitName(name: string): string[] {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) throw new AccountError('account name is empty');
  for (const p of parts) {
    if (p === '.' || p === '..' || p.includes('/') || p.includes('\\')) {
      throw new AccountError(`invalid name segment: ${JSON.stringify(p)}`);
    }
  }
  return parts;
}

export interface AccountTreeNode {
  name: string;
  children: Record<string, AccountTreeNode>;
  id?: string;
  account_name?: string;
}

export class AccountManager {
  accounts = new Map<string, Account>();
  private byPath = new Map<string, string>(); // parts.join(' ') -> id
  private nextId = 1;                          // 0 is reserved for mobile

  /** Load the registry and replay every fill to rebuild positions. */
  async load(): Promise<void> {
    this.accounts.clear();
    this.byPath.clear();
    this.nextId = 1;
    const reg = await getMeta<Registry>('registry', { next_id: 1, accounts: {} });
    this.nextId = reg.next_id || 1;
    for (const [aid, info] of Object.entries(reg.accounts || {})) {
      this.register(aid, info.name, info.parts);
    }
    if (!this.accounts.has(MOBILE_ID)) {
      this.register(MOBILE_ID, MOBILE_NAME, [MOBILE_NAME]);
      await this.save();
    }
    await this.replay();
  }

  /** Rebuild every account's positions from the fill ledger. */
  async replay(): Promise<void> {
    for (const a of this.accounts.values()) a.positions.clear();
    for (const f of await allFills()) {
      const acct = this.accounts.get(f.account_id) ?? this.accounts.get(MOBILE_ID);
      if (!acct) continue;
      this.applyToPositions(acct, f);
    }
  }

  applyToPositions(acct: Account, f: FillRow): void {
    let ps = acct.positions.get(f.ticker);
    if (!ps) {
      ps = blankPos();
      acct.positions.set(f.ticker, ps);
    }
    applyFill(ps, f.side, f.mq, f.yes_price_mc, f.fee_cash);
  }

  private register(id: string, name: string, parts: string[]): Account {
    const acct: Account = { id, name, parts, positions: new Map() };
    this.accounts.set(id, acct);
    this.byPath.set(parts.join(' '), id);
    return acct;
  }

  private async save(): Promise<void> {
    const reg: Registry = { next_id: this.nextId, accounts: {} };
    for (const [aid, a] of this.accounts) reg.accounts[aid] = { name: a.name, parts: a.parts };
    await setMeta('registry', reg);
  }

  get(id: string): Account | undefined {
    return this.accounts.get(id);
  }

  leaves(): Account[] {
    return [...this.byPath.values()].map((id) => this.accounts.get(id)!).filter(Boolean);
  }

  isLeaf(parts: string[]): boolean {
    return this.byPath.has(parts.join(' '));
  }

  /** A path is a branch if some existing leaf has it as a strict prefix. */
  isBranch(parts: string[]): boolean {
    const prefix = parts.join(' ') + ' ';
    for (const key of this.byPath.keys()) if (key.startsWith(prefix)) return true;
    return false;
  }

  /** Create a new leaf account; the path must be fresh and no ancestor may already be a leaf. */
  async create(name: string): Promise<Account> {
    const parts = splitName(name);
    if (this.isLeaf(parts)) throw new AccountError(`account already exists: ${name}`);
    if (this.isBranch(parts)) throw new AccountError(`${name} is a branch node (has child accounts)`);
    for (let k = 1; k < parts.length; k++) {
      if (this.isLeaf(parts.slice(0, k))) {
        throw new AccountError(
          `ancestor "${parts.slice(0, k).join(' ')}" is a leaf account and cannot hold sub-accounts`,
        );
      }
    }
    const id = fmtId(this.nextId);
    this.nextId++;
    const acct = this.register(id, parts.join(' '), parts);
    await this.save();
    return acct;
  }

  /** Remove a leaf account; its fills are NOT deleted (the caller rebooks them) and `nextId` is never reused. */
  async remove(id: string): Promise<void> {
    if (id === MOBILE_ID) throw new AccountError('the mobile residual cannot be deleted');
    const acct = this.accounts.get(id);
    if (!acct) throw new AccountError(`no such account: ${id}`);
    this.accounts.delete(id);
    this.byPath.delete(acct.parts.join(' '));
    await this.save();
  }

  tree(): AccountTreeNode {
    const root: AccountTreeNode = { name: '', children: {} };
    for (const acct of this.accounts.values()) {
      let node = root;
      acct.parts.forEach((seg, i) => {
        let child = node.children[seg];
        if (!child) {
          child = { name: seg, children: {} };
          node.children[seg] = child;
        }
        if (i === acct.parts.length - 1) {
          child.id = acct.id;
          child.account_name = acct.name;
        }
        node = child;
      });
    }
    return root;
  }

  listLeaves(): { id: string; name: string }[] {
    return this.leaves()
      .map((a) => ({ id: a.id, name: a.name }))
      .sort((x, y) => x.name.localeCompare(y.name));
  }
}

/** Roll account rows up the name tree; a leaf node carries its account id. */
export function pnlTree(accounts: AccountRow[]): PnlTreeNode {
  const tree: PnlTreeNode = { name: '', children: {}, pnlCash: 0, positionMq: 0 };
  for (const a of accounts) {
    tree.pnlCash += a.pnlCash;
    tree.positionMq += a.positionMq;
    let node = tree;
    a.parts.forEach((seg, i) => {
      let child = node.children[seg];
      if (!child) {
        child = { name: seg, children: {}, pnlCash: 0, positionMq: 0 };
        node.children[seg] = child;
      }
      child.pnlCash += a.pnlCash;
      child.positionMq += a.positionMq;
      if (i === a.parts.length - 1) child.id = a.id;
      node = child;
    });
  }
  return tree;
}
